/**
 * Currency formatting utilities — single source of truth for $ display.
 *
 * makeFmt(calculation) returns a function (usdAmount, opts) => formatted string
 * that respects the calculation's chosen target currency (calculation.forex).
 *
 * Components that previously had a local `fmt$` should import this and call:
 *     const fmt$ = useMemo(() => makeFmt(calculation), [calculation]);
 *
 * Components without access to a calculation can call makeFmt(null) to get
 * a USD formatter (legacy behaviour).
 */

const DEFAULT_FX = { rate_per_usd: 1, symbol: '$', target_currency: 'USD' };

export function getForex(calculation) {
  return calculation?.forex || DEFAULT_FX;
}

/** Returns a function (usdAmount, opts?) => formatted string. */
export function makeFmt(calculation) {
  const fx = getForex(calculation);
  const { rate_per_usd = 1, symbol = '$' } = fx;

  return function fmt(usd, opts = {}) {
    const { compact = false, decimals } = opts;
    const v = Number(usd || 0) * rate_per_usd;
    if (!isFinite(v)) return `${symbol}0`;

    if (compact) {
      const abs = Math.abs(v);
      if (abs >= 1_000_000_000) return `${symbol}${(v / 1_000_000_000).toFixed(1)}B`;
      if (abs >= 1_000_000)     return `${symbol}${(v / 1_000_000).toFixed(1)}M`;
      if (abs >= 1_000)         return `${symbol}${(v / 1_000).toFixed(0)}k`;
      return `${symbol}${v.toFixed(0)}`;
    }
    const d = decimals ?? 0;
    return `${symbol}${v.toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d })}`;
  };
}

/** Convenience: format a USD amount as compact currency (k/M/B). */
export function fmtCompact(calculation, usd) {
  return makeFmt(calculation)(usd, { compact: true });
}

/** Returns the active currency symbol, e.g. "$" / "₹" / "د.إ". */
export function currencySymbol(calculation) {
  return getForex(calculation).symbol || '$';
}

/** Returns the active currency code, e.g. "USD" / "INR" / "AED". */
export function currencyCode(calculation) {
  return getForex(calculation).target_currency || 'USD';
}
